import { FC, useCallback, useMemo } from 'react'
import { isIOS } from 'react-device-detect'
import { CreditCardPanePreset } from 'components/features/creditCard/creditCardPanes/CreditCardPanePreset'
import { cardPaymentMethodsSelector, nextCardSelector } from 'components/features/creditCard/creditCardStore/selectors'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { currentCardSelector } from 'components/features/creditCard/creditCardStore/selectors'
import Separator from 'components/atoms/Separator/Separator'
import {
  CardDetailsPaneButton,
  BalanceDetailsPaneButton,
  ChangePinPaneButton,
  CardStatementsPaneButton,
  CardPaymentsPaneButton,
  PaymentMethodsPaneButton,
  ReplaceCardPaneButton,
  LockCardPaneButton,
  AddPaymentMethodsPaneButton,
  PayCardAmountPaneButton,
  AddCardToWalletPaneButton,
  InviteFriendsPaneButton,
  NewCoachingSessionPaneButton,
  AutoPayPaneCTAButton,
  CardMenuPaneButton,
  BACK_BUTTON_PROPS,
  CANCEL_BUTTON_PROPS,
  KillAllPanesButton,
} from 'components/features/pfm/Button/FloatingPaneButtons'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import { killAllFloatingPanesActionCreator } from 'actions/floatingPanesActions'
import FloatingPaneNotification from 'components/features/pfm/FloatingPaneNotification/FloatingPaneNotification'
import { ActivateCardActionButton } from 'components/features/pfm/Button/ActionButtons'

const CardMenuPane: FC<{}> = () => {
  const dispatch = useStoreDispatch()
  const paymentMethods = useStoreSelector(cardPaymentMethodsSelector)
  const card = useStoreSelector(currentCardSelector)
  const replacementCard = useStoreSelector(nextCardSelector)
  const cardToActivate = replacementCard || card
  const cardStatus = useMemo(() => card?.status ?? null, [card?.status])
  const hasPaymentMethods = !!paymentMethods && paymentMethods?.length > 0
  const activateIsEnabled = !!cardToActivate?.uid && (cardToActivate.status === 'awaiting_activation' || (cardToActivate.status === 'suspended' && !!cardToActivate.replacement_request))
  const lockCardIsEnabled = (cardStatus === 'active' || cardStatus === 'suspended') && card?.replacement_request?.reason !== 'compromised'
  const unlockIsEnabled = !!card?.uid && cardStatus === 'suspended'
  const replaceIsEnabled = (cardStatus === 'active' || cardStatus === 'suspended') && !card?.replacement_request

  const handleCancel = useCallback(() => {
    dispatch(killAllFloatingPanesActionCreator())
  }, [dispatch])


  const handleSeeTransactions = useCallback(() => {
    // TODO - Set account filter for credit card
    // dispatch(setAccountFiltersActionCreator)
    handleCancel()
  }, [handleCancel])


  const appleWalletChild = useMemo(() => isIOS && (
    <li>
      <AddCardToWalletPaneButton text='Add card to Apple Wallet' renderAsLink disabled />
    </li>
  ), [])

  const activateLinkChild = useMemo(() => activateIsEnabled && !!cardToActivate?.uid && (
    <li>
      <ActivateCardActionButton text='Activate Your Card' uid={ cardToActivate.uid } beforeOnClick={ handleCancel } renderAsLink />
    </li>
  ), [card?.uid, cardStatus, handleCancel])

  const notificationChild = useMemo(() => (!lockCardIsEnabled || !replaceIsEnabled) && (
    <FloatingPaneNotification
      theme='info'
      style='textOnly'
      text='Some controls are disabled until your new card is activated'
      iconSlug='report'
      small
    />
  ), [lockCardIsEnabled, replaceIsEnabled])

  return (
    <CreditCardPanePreset
      title='Manage Your Card'
      secondaryButton={ null }
      tertiaryButton={ <KillAllPanesButton { ...CANCEL_BUTTON_PROPS } text='Close' /> }
    >
      <h2>What would you like to do?</h2>
      <nav>
        <ul>
          <Separator title='Pay Off Your Card' asListItem large theme='midnight' />
          <li>
            <PayCardAmountPaneButton text='Make a payment' renderAsLink />
          </li>
          <li>
            <AutoPayPaneCTAButton renderAsLink backButton={ <CardMenuPaneButton { ...BACK_BUTTON_PROPS } /> } />
          </li>
          <li>
            { !!hasPaymentMethods ? (
              <PaymentMethodsPaneButton text='Manage payment methods' renderAsLink />
            ) : (
              <AddPaymentMethodsPaneButton text='Add payment method' renderAsLink />
            ) }
          </li>
          <li>
            <CardPaymentsPaneButton text='View your payments' renderAsLink />
          </li>
        </ul>

        <ul>
          <Separator title='See Details &amp; Info' asListItem large theme='midnight' />
          <li>
            <CardDetailsPaneButton text='View your card details' renderAsLink />
          </li>
          <li>
            <BalanceDetailsPaneButton text='View your balance details' renderAsLink />
          </li>
          <li>
            <ChangePinPaneButton text={ 'View your card\'s PIN #' } renderAsLink />
          </li>
          <li>
            <NavigationLink to='transactions' onClick={ handleSeeTransactions }>
              See all card transactions
            </NavigationLink>
          </li>
          <li>
            <CardStatementsPaneButton text='View your statements' renderAsLink />
          </li>
        </ul>

        <ul>
          <Separator title='Control Your Card' asListItem large theme='midnight' />
          { activateLinkChild }
          { appleWalletChild }
          <li>
            { unlockIsEnabled ? (
              <LockCardPaneButton text='Unlock your card' renderAsLink />
            ) : (
              <LockCardPaneButton text='Lock your card' renderAsLink disabled={ !lockCardIsEnabled } />
            ) }
          </li>
          <li>
            <ReplaceCardPaneButton text='Replace card' renderAsLink disabled={ !replaceIsEnabled } />
          </li>
          { notificationChild }
        </ul>

        <ul>
          <Separator title='Rewards &amp; Benefits' asListItem large theme='midnight' />
          <li>
            <NavigationLink to='rewards' onClick={ handleCancel } >
              Track your rewards
            </NavigationLink>
          </li>
          <li>
            <NavigationLink to='creditscore' onClick={ handleCancel } >
              Track your credit score
            </NavigationLink>
          </li>
          <li>
            <NewCoachingSessionPaneButton text='Schedule a Coaching session' renderAsLink />
          </li>
          <li>
            <InviteFriendsPaneButton text='Invite friends' renderAsLink />
          </li>
        </ul>
      </nav>
    </CreditCardPanePreset>
  )
}

export default CardMenuPane
